import GLOBAL from "context/global";

function resolveWsBase() {
  const explicit = process.env.REACT_APP_DATA_MAPPING_WS_URL;
  if (explicit) return explicit.replace(/\/$/, "");
  const apiBase = process.env.REACT_APP_DATA_MAPPING_API_URL || "";
  return apiBase
    .replace(/^https:\/\//, "wss://")
    .replace(/^http:\/\//, "ws://")
    .replace(/\/$/, "");
}

// Close codes the backend uses (app/api/websocket/chat.py). 4401 means "authenticate"; 4004 means "no
// such session for you" and covers a tenant mismatch as well as a missing session — deliberately
// indistinguishable, so a leaked session id reveals nothing.
export const WS_CLOSE_UNAUTHENTICATED = 4401;
export const WS_CLOSE_NOT_FOUND = 4004;

// Why the handshake carries the token as a subprotocol: browsers cannot set headers on `new WebSocket`,
// and a query parameter would put the token in the URL, the Referer, browser history and ALB access
// logs. `Sec-WebSocket-Protocol` is the one channel available, and a JWT is a valid subprotocol token
// (base64url plus ".", "-" and "_" are all HTTP token characters). The server echoes "bearer" back on
// accept; a browser rejects a handshake that does not confirm the subprotocol it offered.
const BEARER_SUBPROTOCOL = "bearer";

export class DataMappingWebSocket {
  constructor(sessionId, onMessage, onStatusChange) {
    this.sessionId = sessionId;
    this.onMessage = onMessage;
    this.onStatusChange = onStatusChange;
    this.ws = null;
  }

  connect() {
    const base = resolveWsBase();
    if (!base) {
      // Surfaced to the caller, not just the console: without this the chat silently never connects and
      // the UI shows a permanently-disconnected badge with no explanation.
      const message = "Chat is unavailable: REACT_APP_DATA_MAPPING_API_URL is not configured.";
      console.warn(`DataMappingWebSocket: ${message}`);
      this.onStatusChange?.(false, { reason: message });
      return;
    }

    const token = GLOBAL.accessToken;
    if (!token) {
      const message = "Chat is unavailable: you are not signed in.";
      this.onStatusChange?.(false, { reason: message });
      return;
    }

    this.ws = new WebSocket(`${base}/ws/sessions/${this.sessionId}/chat`, [
      BEARER_SUBPROTOCOL,
      token,
    ]);

    this.ws.onopen = () => this.onStatusChange?.(true);
    // The close code is passed through because auth failures arrive this way and are otherwise
    // invisible: without it, a rejected token looks exactly like an ordinary disconnect and the user is
    // told only "disconnected".
    this.ws.onclose = (event) =>
      this.onStatusChange?.(false, { code: event?.code, reason: describeClose(event?.code) });
    this.ws.onerror = () => this.onStatusChange?.(false, {});
    this.ws.onmessage = (event) => {
      try {
        this.onMessage?.(JSON.parse(event.data));
      } catch {
        console.error("DataMappingWebSocket: failed to parse message", event.data);
      }
    };
  }

  get isOpen() {
    return this.ws?.readyState === WebSocket.OPEN;
  }

  /**
   * @param {string} text     What the model is asked.
   * @param {object} [opts]
   * @param {string} [opts.display]  What the UI shows instead, for a turn the app started itself.
   * @param {boolean} [opts.quiet]   Suppress the not-connected notice. Used for app-initiated turns:
   *   that notice is written for someone who typed something, and showing it for a message the user
   *   never wrote reads as a bug in the page.
   */
  send(text, { display, quiet = false, ephemeral = false } = {}) {
    if (this.ws?.readyState !== WebSocket.OPEN) {
      // Previously dropped in silence, so a message typed before the socket opened just vanished.
      if (!quiet) {
        this.onStatusChange?.(false, {
          reason: "Not connected — your message was not sent. Reload the page and try again.",
        });
      }
      return false;
    }
    // The access token rides on every message as well as on the handshake, and both are needed for
    // different reasons: the handshake one authorizes the connection, while the per-message one is what
    // the task-aware tools present to the Java API. Per-message because tokens expire and the app
    // refreshes them silently (useRefreshTokens), so each turn carries the freshest one the browser
    // holds. The backend verifies it, checks it belongs to the same org as the session, binds it for one
    // turn and never persists it — see app/session/context.py::set_current_user_token.
    // `display` is omitted entirely when absent rather than sent as null: the backend treats its
    // PRESENCE as the "app started this turn" marker (see chat.py), so a null would be ambiguous.
    // `ephemeral` asks the backend to answer the turn without saving it to the session's transcript or
    // history (see chat.py) — for a reply that goes somewhere other than the chat. Omitted unless set, so an
    // ordinary turn's payload is unchanged.
    this.ws.send(
      JSON.stringify({
        text,
        display: display || undefined,
        ephemeral: ephemeral || undefined,
        token: GLOBAL.accessToken || undefined,
      })
    );
    return true;
  }

  disconnect() {
    this.ws?.close();
    this.ws = null;
  }
}

function describeClose(code) {
  if (code === WS_CLOSE_UNAUTHENTICATED) {
    return "Your session sign-in was rejected. Reload the page to sign in again.";
  }
  if (code === WS_CLOSE_NOT_FOUND) {
    return "This mapping session is not available to your organisation.";
  }
  return undefined;
}

export default DataMappingWebSocket;
